import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaFacebook, FaTwitter, FaInstagram, FaLinkedin, FaYoutube } from 'react-icons/fa';
import logoImg from '../assets/logo.svg';

const socials = [
  { Icon: FaFacebook, color: 'from-blue-500 to-blue-600' },
  { Icon: FaTwitter, color: 'from-sky-400 to-sky-500' },
  { Icon: FaInstagram, color: 'from-pink-500 to-orange-500' },
  { Icon: FaLinkedin, color: 'from-blue-600 to-blue-700' },
  { Icon: FaYoutube, color: 'from-red-500 to-red-600' },
];

export default function Footer() {
  const [email, setEmail] = useState('');

  const handleSubscribe = () => {
    if (email && email.includes('@')) {
      alert('Thank you for subscribing! 🎉');
      setEmail('');
    } else {
      alert('Please enter a valid email address');
    }
  };

  return (
    <footer className="relative bg-white dark:bg-black border-t border-gray-200 dark:border-gray-800 pt-20 pb-10 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 lg:grid-cols-12 gap-12 mb-16">
          {/* Brand */}
          <div className="lg:col-span-5">
            <div className="flex items-center space-x-3 mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-orange-500 blur-xl opacity-30 animate-pulse rounded-full pointer-events-none" />
                <motion.img src={logoImg} alt="Logo" className="w-14 h-14 object-contain relative z-10"
                  style={{ filter: 'drop-shadow(0 0 10px rgba(249,115,22,0.8))' }}
                  whileHover={{ rotate: 180, scale: 1.1 }} transition={{ duration: 0.6 }} />
              </div>
              <div>
                <div className="text-3xl font-bold">
                  <span className="text-gray-900 dark:text-white">bottom</span>
                  <span className="text-orange-500">funnel</span>
                </div>
                <div className="text-xs text-gray-500 tracking-widest uppercase -mt-1">Maximizing Conversions</div>
              </div>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-8 leading-relaxed max-w-md">
              Your trusted partner for growth, income, and business management. We turn digital strategies into measurable results and lasting success.
            </p>
            <h3 className="text-gray-900 dark:text-white font-bold text-lg mb-3">Stay Updated</h3>
            <div className="flex flex-col sm:flex-row gap-3">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-5 py-3 bg-gray-50/50 dark:bg-gray-900/50 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:border-cyan-500 text-gray-900 dark:text-white placeholder-gray-500 transition-all backdrop-blur-sm" />
              <motion.button onClick={handleSubscribe} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl font-semibold text-white hover:shadow-lg hover:shadow-cyan-500/50 transition-all">
                Subscribe
              </motion.button>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h3 className="text-gray-900 dark:text-white font-bold mb-6 text-lg">Quick Links</h3>
            <ul className="space-y-3">
              {['Home', 'Services', 'Process', 'Why Us', 'Testimonials', 'FAQ'].map((link) => (
                <li key={link}>
                  <motion.a href={`#${link.toLowerCase().replace(' ', '-')}`}
                    whileHover={{ x: 5, color: '#0ea5e9' }}
                    className="text-gray-600 dark:text-gray-400 hover:text-cyan-400 transition-all block">{link}</motion.a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <h3 className="text-gray-900 dark:text-white font-bold mb-6 text-lg">Services</h3>
            <ul className="space-y-3">
              {['Social Media', 'Branding', 'Video Production', 'Web Development', 'Advertising', 'Consulting'].map((s) => (
                <li key={s}>
                  <motion.a href="#services" whileHover={{ x: 5, color: '#f97316' }}
                    className="text-gray-600 dark:text-gray-400 hover:text-orange-400 transition-all block">{s}</motion.a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <h3 className="text-gray-900 dark:text-white font-bold mb-6 text-lg">Contact</h3>
            <ul className="space-y-4 text-gray-600 dark:text-gray-400 mb-8">
              <li className="flex items-start gap-3">
                <span className="text-cyan-400">📧</span> 
                <a href="mailto:replybottomfunnel@gmail.com" className="hover:text-cyan-400 transition-colors">
                  replybottomfunnel@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-cyan-400">📞</span> 
                <a href="https://wa.me/919473448626" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors">
                  +91 9473448626
                </a>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-cyan-400">📍</span> 
                <a href="https://maps.app.goo.gl/aa59VJh6RQ1MaGZ66" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors">
                  SKMCH GATE NO:-1 , Muzaffarpur, Bihar . 842004
                </a>
              </li>
            </ul>
            <div className="flex gap-3 flex-wrap">
              {socials.map(({ Icon, color }, i) => (
                <motion.a key={i} href="#" whileHover={{ y: -5, scale: 1.1 }} whileTap={{ scale: 0.95 }}
                  className={`w-12 h-12 bg-gradient-to-br ${color} rounded-xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all`}>
                  <Icon className="text-xl text-white" />
                </motion.a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-gray-200 dark:border-gray-800 pt-10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">&copy; {new Date().getFullYear()} BottomFunnel. All rights reserved.</p>
          <div className="flex gap-6 text-sm text-gray-500">
            <motion.a href="#" whileHover={{ color: '#0ea5e9' }} className="transition-colors">Privacy Policy</motion.a>
            <motion.a href="#" whileHover={{ color: '#0ea5e9' }} className="transition-colors">Terms of Service</motion.a>
          </div>
        </div>
      </div>
    </footer>
  );
}
